import { Chart, registerables } from "chart.js";
import { useEffect, useState } from "react";
import { Col, Container, Pagination, Row, Table } from "react-bootstrap";
import { Bar, Pie } from "react-chartjs-2";
import { AdminInstance } from "../utils/adminClass";
import AnalyticsHorizontalPinChart from "./analyticsHorizontalPinChart";
import { GlobalUserAnalyticsDashboard } from "./globalUserAnalyticsDashboard";
import { ZipCodeInputComponent } from "./zipCodeInputComponent";

Chart.register(...registerables);

const ITEMS_PER_PAGE = 20;

//format titles from camelCase to Title Case and remove "Count"
function camelToTitleCase(camel: string) {
  const withoutCount = camel.replace("Count", "");
  const words = withoutCount.split(/(?=[A-Z])/);
  const titleCase = words.map((word: any) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  return titleCase;
}

export const AdminAppAnalytics = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [followerData, setFollowerData] = useState({ labels: [], primaryData: [] });
  const [zipCodes, setZipCodes] = useState<any[]>([]);
  const [newestUsers, setNewestUsers] = useState<any[]>([]);
  const [aggregatedUserData, setAggregatedUserData] = useState<any>([]);
  // table state
  const [currentPage, setCurrentPage] = useState(1);
  // table state

  // Calculate total pages
  const totalPages = Math.ceil(newestUsers?.length / ITEMS_PER_PAGE);
  // Get current page products
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentNewUsers = newestUsers.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    getFollowerGainData();
    getNewestUsers();
    getAggregatedUserAnalytics();
  }, []);

  const getUserData = async () => {
    const res = await AdminInstance.getAppLocationAnalytics(zipCodes);
    setUsers(res);
    const uniqueZipCodes = Array.from(new Set(res.map((user: any) => user.homeZipCode)));
  };

  const getFollowerGainData = async () => {
    const res = await AdminInstance.getUserSignUpAnalytics();
    setFollowerData(res);
  };

  const getNewestUsers = async () => {
    const res = await AdminInstance.getNewestUsers();
    setNewestUsers(res);
  };

  const getAggregatedUserAnalytics = async () => {
    const res = await AdminInstance.aggregatedUserAppAnalytics();
    setAggregatedUserData(res);
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleJsonDataView = (user: any) => {
    console.log("Viewing JSON Data", user);
  };

  const renderBarChart = (title: string, labels: string[], data: any[]) => {
    return (
      <Bar
        data={{
          labels: labels,
          datasets: [
            {
              label: title,
              data: data,
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderColor: "rgba(75, 192, 192, 1)",
              borderWidth: 1,
            },
          ],
        }}
        options={{ maintainAspectRatio: false }}
      />
    );
  };

  const renderPieChart = (title: string, labels: any, data: any) => {
    return (
      <Pie
        data={{
          labels: labels,
          datasets: [
            {
              label: title,
              data: data,
              backgroundColor: [
                "rgba(255, 99, 132, 0.2)",
                "rgba(54, 162, 235, 0.2)",
                "rgba(255, 206, 86, 0.2)",
                "rgba(75, 192, 192, 0.2)",
                "rgba(153, 102, 255, 0.2)",
              ],
              borderColor: [
                "rgba(255, 99, 132, 1)",
                "rgba(54, 162, 235, 1)",
                "rgba(255, 206, 86, 1)",
                "rgba(75, 192, 192, 1)",
                "rgba(153, 102, 255, 1)",
              ],
              borderWidth: 1,
            },
          ],
        }}
        options={{ maintainAspectRatio: false }}
      />
    );
  };

  const getLabelsAndData = (dataObj: any) => {
    const labels = Object.keys(dataObj);
    const data = Object.values(dataObj);
    return { labels, data };
  };

  return (
    <Container className="py-4">
      <Row>
        <h3>Daily Sign up Count</h3>
        <AnalyticsHorizontalPinChart
          title={"Users Per Day"}
          labels={followerData.labels}
          primaryData={followerData.primaryData}
        />
      </Row>
      <Row>
        <h3>Last 15 days of users</h3>
        <div className="table-responsive">
          <Table responsive className="table">
            <thead>
              <tr>
                <th>User name</th>
                <th>Join Date</th>
                <th>Zip Code</th>
                <th>Opted In</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentNewUsers?.map((user: any, index: number) => (
                <tr key={index}>
                  <td>
                    {user.username} {user.firstName}
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>{user.homeZipCode}</td>
                  <td>{user?.nevadaOptIn?.optIn ? user?.nevadaOptIn?.optIn.toString() : "null"}</td>
                  <td>
                    <button onClick={() => handleJsonDataView(user)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="pagination">
            <span>
              Showing {indexOfFirstItem + 1} to {indexOfLastItem} of {newestUsers?.length} results
            </span>
            <Pagination>
              <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
              <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
              {[...Array(totalPages).keys()].map((number) => (
                <Pagination.Item
                  key={number + 1}
                  active={number + 1 === currentPage}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
              <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        </div>
      </Row>
      <Row>
        <h3>Aggregated User Analytics</h3>
        <Col md={6}>
          {renderBarChart(
            "Gender Distribution",
            Object.keys(aggregatedUserData.genderCount || {}),
            Object.values(aggregatedUserData.genderCount || {}),
          )}
        </Col>
        <Col md={6}>
          {renderBarChart(
            "Age Ranges",
            Object.keys(aggregatedUserData.ageRanges || {}),
            Object.values(aggregatedUserData.ageRanges || {}),
          )}
        </Col>
        <Col md={6}>
          {renderBarChart(
            "Experience Levels",
            Object.keys(aggregatedUserData.experienceLevels || {}),
            Object.values(aggregatedUserData.experienceLevels || {}),
          )}
        </Col>
        <Col md={6}>
          {renderPieChart(
            "Preferred Strains",
            Object.keys(aggregatedUserData.preferredStrains || {}),
            Object.values(aggregatedUserData.preferredStrains || {}),
          )}
        </Col>
        {/* render a chart for zip codes */}
        <Col md={6}>
          {renderBarChart(
            "Zip Code Distribution",
            Object.keys(aggregatedUserData.zipCodeCount || {}),
            Object.values(aggregatedUserData.zipCodeCount || {}),
          )}
        </Col>
      </Row>
      <Row>
        <h3>Global Location Analytics</h3>
      </Row>
      <ZipCodeInputComponent setZipCodes={setZipCodes} zipCodes={zipCodes} />
      <button onClick={getUserData}>Initialize Business Analytics</button>
      <GlobalUserAnalyticsDashboard users={users} zipCodes={zipCodes} />
    </Container>
  );
};
